import { useState } from "react";
import { Search, MapPin, Filter, Plus, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { VoiceSearchButton } from "@/components/VoiceSearchButton";

// US States for quick access
const POPULAR_STATES = ["TX", "CA", "FL", "NY", "IL", "PA", "OH", "GA", "NC", "MI"];

export function MarketplaceSearchHeader({
  listingType,
  config,
  searchQuery,
  onSearchChange,
  filters,
  onOpenFilters,
  onOpenPostModal,
  activeFilterCount,
  heroImage,
}) {
  const [showQuickLocation, setShowQuickLocation] = useState(false);
  
  // Get placeholder based on listing type
  const getPlaceholder = () => {
    switch (listingType) {
      case "vehicles":
        return "Search by make, model, year, VIN...";
      case "real_estate":
        return "Search by address, neighborhood, ZIP...";
      case "rvs":
        return "Search by make, model, RV type...";
      case "furniture":
        return "Search by category, style, brand...";
      default:
        return "Search listings...";
    }
  };
  
  return (
    <div className="glass-card rounded-xl p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Input - Main */}
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={getPlaceholder()}
            className="w-full pl-12 pr-14 h-12 bg-white/5 border-white/10 text-white rounded-xl text-base placeholder:text-[#64748B]"
            data-testid="marketplace-search-input"
          />
          {/* Voice Search Button */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <VoiceSearchButton 
              onResult={(text) => onSearchChange(text)} 
              size="small"
            />
          </div>
        </div>
        
        {/* Quick Location */}
        <div className="flex items-center gap-2">
          {/* Location indicator */}
          {(filters.state || filters.city || filters.zip_code) && (
            <Badge 
              className="bg-[#10B981]/20 text-[#10B981] px-3 py-1.5 flex items-center gap-1"
            >
              <MapPin className="w-3 h-3" />
              {filters.city || filters.state || filters.zip_code}
              <button 
                onClick={() => {
                  // Clear location filters - this should be handled by parent
                }}
                className="ml-1 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {/* Filters Button */}
          <Button
            onClick={onOpenFilters}
            variant="outline"
            className="h-12 px-4 border-white/10 text-white hover:bg-white/10 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-[#7C3AED] text-white text-xs flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
          
          {/* Post Button */}
          {config.postButton && (
            <Button
              onClick={onOpenPostModal}
              className="h-12 px-4 font-semibold flex items-center gap-2"
              style={{ backgroundColor: config.color }}
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">{config.postButton}</span>
              <span className="sm:hidden">Post</span>
            </Button>
          )}
        </div>
      </div>
      
      {/* Active Filter Chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/10">
          <span className="text-xs text-[#94A3B8]">Active filters:</span>
          
          {filters.state && (
            <FilterChip 
              label={`State: ${filters.state}`} 
              onRemove={() => {}} 
              color="#10B981"
            />
          )}
          {filters.city && (
            <FilterChip 
              label={`City: ${filters.city}`} 
              onRemove={() => {}} 
              color="#00E5FF"
            />
          )}
          {filters.min_price && (
            <FilterChip 
              label={`Min: $${filters.min_price.toLocaleString()}`} 
              onRemove={() => {}} 
              color="#F59E0B"
            />
          )}
          {filters.max_price && (
            <FilterChip 
              label={`Max: $${filters.max_price.toLocaleString()}`} 
              onRemove={() => {}} 
              color="#F59E0B"
            />
          )}
          {filters.condition && (
            <FilterChip 
              label={filters.condition} 
              onRemove={() => {}} 
              color="#7C3AED"
            />
          )}
          {filters.make && (
            <FilterChip 
              label={filters.make} 
              onRemove={() => {}} 
              color="#EF4444"
            />
          )}
          {filters.model && (
            <FilterChip 
              label={filters.model} 
              onRemove={() => {}} 
              color="#EF4444"
            />
          )}
          {filters.property_type && (
            <FilterChip 
              label={filters.property_type} 
              onRemove={() => {}} 
              color="#EC4899"
            />
          )}
          {filters.min_beds && (
            <FilterChip 
              label={`${filters.min_beds}+ beds`} 
              onRemove={() => {}} 
              color="#EC4899"
            />
          )}
          {filters.rv_type && (
            <FilterChip 
              label={filters.rv_type} 
              onRemove={() => {}} 
              color="#06B6D4"
            />
          )}
          {filters.category && (
            <FilterChip 
              label={filters.category} 
              onRemove={() => {}} 
              color="#8B5CF6"
            />
          )}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, onRemove, color }) {
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {label}
      <button onClick={onRemove} className="hover:opacity-70">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export default MarketplaceSearchHeader;
